import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useApi } from "providers/ApiProvider";
import { useUser } from "providers/UserProvider";
import { useUserStore } from "~/hooks/useUserStore";

interface ClinicContextValue {
  clinic: any;
  clinicConfig: ClinicConfigItem[];
  isMasking: boolean;
}

interface ClinicConfigItem {
  id: number;
  groupName: string;
  codeName: string;
  codeValue: string;
}

const ClinicContext = createContext<ClinicContextValue | null>(null);

export function ClinicProvider({ children }: { children: ReactNode }) {
  const { clinicsApi } = useApi();
  const { user } = useUser();
  const clinicId = useMemo(() => {
    return user?.clinic?.id;
  }, [user?.clinic]);
  const userStore = useUserStore();
  const [clinic, setClinic] = useState<any>({});
  const [clinicConfig, setClinicConfig] = useState<ClinicConfigItem[]>([]);

  const loadClinic = useCallback(async () => {
    let res;
    try {
      res = await clinicsApi.getClinic(clinicId);
    } catch (e) {
      return;
    }

    const payload = await res.data;
    setClinic(payload.data);
  }, [clinicsApi, clinicId]);

  const loadClinicConfigs = useCallback(async () => {
    const res = await clinicsApi.getConfigs();
    const configData = await res.data;

    setClinicConfig(configData.data);
  }, [clinicsApi]);

  useEffect(() => {
    if (!clinicId) return;

    loadClinic();
    loadClinicConfigs();
  }, [loadClinic, loadClinicConfigs, clinicId]);

  useEffect(() => {
    userStore.updateClinicConfigData(clinicConfig);
  }, [clinicConfig]);

  const isMasking = useMemo(() => {
    if (!clinicConfig || clinicConfig.length === 0) return false;

    const privacyConfig = clinicConfig.find(
      (config) =>
        config.groupName === "privacy" && config.codeName === "masking"
    );

    return privacyConfig ? privacyConfig.codeValue === "True" : false;
  }, [clinicConfig]);

  return (
    <ClinicContext.Provider value={{ clinic, isMasking, clinicConfig }}>
      {children}
    </ClinicContext.Provider>
  );
}

export function useClinic() {
  return useContext(ClinicContext) as ClinicContextValue;
}
